import { applyOutputNodeSlotMetadata } from '../graph/nodeDefs.js';
import type { GraphNode } from '../graph/nodeDefs.js';
import { nodeDefsForKind, nodeDefsForSlot } from '../graph/nodeRegistry.js';
import type { IntrinsicSizeMode } from '../graph/graphRuntime.js';
import { formatStableIdDebugLabel } from '../graph/debugLabels.js';
import {
  findLayerGraphLibraryItem,
  type LibraryGeometryDef,
  type LibraryMaterialDef,
} from '../scene/layerState.js';
import { autoPlaceNodes } from '../graph/autoPlace.js';
import type { AppState, EditorGraphMode, EditorGraphSlot, EditorContext, RuntimeGraphKind } from '../state/appState.js';
import {
  getLayerGraphLibrariesFor,
  resolveGeometrySlotFor,
  resolveMaterialSlotFor,
} from '../state/layerGraphSelectors.js';
import { findFirstLayerNodeIds } from '../scene/sceneQueries.js';
import { cloneEdges, cloneNodes } from './documentClone.js';

function shouldAutoPlaceNodes(nodes: GraphNode[]): boolean {
  if (nodes.length <= 1) return false;
  const positions = new Set(nodes.map(node => `${node.x},${node.y}`));
  return positions.size <= 1;
}

function lookupLibraryItemFor(appState: AppState, kind: 'geometry' | 'material', id: string) {
  return findLayerGraphLibraryItem(getLayerGraphLibrariesFor(appState), kind, id);
}

export function loadLibraryGraphIntoEditorFor(
  appState: AppState,
  kind: 'geometry' | 'material',
  item: LibraryGeometryDef | LibraryMaterialDef,
): void {
  const nodes = cloneNodes(item.nodes);
  const edges = cloneEdges(item.edges);
  syncOutputNodeMetadata(nodes, kind);

  appState.nodes = nodes;
  appState.edges = edges;
  if (shouldAutoPlaceNodes(appState.nodes)) {
    autoPlaceNodes(appState.nodes, appState.edges, nodeDefsForSlot(kind));
  }
  appState.editorGraphDirty = false;
  appState.selectedNode = null;
  appState.editorGraphSlot = kind;
  appState.editorGraphKind = kind;
}

function syncOutputNodeMetadata(nodes: GraphNode[], slot: EditorGraphSlot): void {
  for (const node of nodes) {
    applyOutputNodeSlotMetadata(node, slot);
  }
}

export function getViewportGraphSlotFor(appState: { editorGraphMode: EditorGraphMode; editorGraphSlot: EditorGraphSlot }): EditorGraphSlot {
  return appState.editorGraphMode === 'auto' ? appState.editorGraphSlot : appState.editorGraphMode;
}

export function getEditorGraphScopeId(context: EditorContext): string | null {
  if (context.mode === 'layer') {
    if (context.layerId) return context.layerId;
    return context.componentId ?? null;
  }
  if (context.mode === 'geometry-library') return context.libraryItemId ? `library:geometry:${context.libraryItemId}` : null;
  if (context.mode === 'material-library') return context.libraryItemId ? `library:material:${context.libraryItemId}` : null;
  if (context.mode === 'data-library') return context.libraryItemId ? `library:data:${context.libraryItemId}` : null;
  if (context.mode === 'event-library') return context.libraryItemId ? `library:event:${context.libraryItemId}` : null;
  if (context.mode === 'query-library') return context.libraryItemId ? `library:query:${context.libraryItemId}` : null;
  return null;
}

export function setEditorGraphModeFor(
  appState: AppState,
  mode: EditorGraphMode,
  hooks: LoadEditorGraphHooks = {},
): void {
  if (mode === appState.editorGraphMode) return;
  hooks.syncEditor?.();
  appState.editorGraphMode = mode;
}

function getLayerOwnerSceneFor(appState: AppState, componentId: string | null | undefined) {
  if (!componentId) return appState.scene;
  return appState.componentLibrary[componentId]?.scene ?? null;
}

function sceneOwnsLayer(scene: ReturnType<typeof getLayerOwnerSceneFor>, layerId: string): boolean {
  if (!scene) return false;
  return Object.values(scene.nodes).some(node => node.kind === 'layer' && node.layerId === layerId);
}

/** Load a specific layer's slot into the node editor. */
export function loadLayerSlotFor(
  appState: AppState,
  componentId: string | null | undefined,
  layerId: string,
  slot: 'geometry' | 'material',
): boolean {
  const ownerScene = getLayerOwnerSceneFor(appState, componentId);
  if (!sceneOwnsLayer(ownerScene, layerId)) return false;

  const layerDef = appState.layerLibrary[layerId];
  if (!layerDef) return false;

  appState.editorContext = {
    mode: 'layer',
    componentId: componentId ?? undefined,
    layerId,
    layerSlot: slot,
  };

  const resolved = slot === 'geometry'
    ? resolveGeometrySlotFor(appState, layerDef.geometry)
    : resolveMaterialSlotFor(appState, layerDef.material);

  if (!resolved) return false;

  appState.nodes = resolved.nodes;
  appState.edges = resolved.edges;
  syncOutputNodeMetadata(appState.nodes, slot);
  if (shouldAutoPlaceNodes(appState.nodes)) autoPlaceNodes(appState.nodes, appState.edges, nodeDefsForSlot(slot));

  appState.editorGraphDirty = false;
  appState.selectedNode = null;
  appState.activeComponentId = componentId ?? null;
  appState.editorGraphSlot = slot;
  appState.editorGraphKind = slot;

  return true;
}

/** Load a component or root scene into the editor by finding a LayerNode. */
export function loadComponentSlotFor(
  appState: AppState,
  componentId: string | null | undefined,
  slot: 'geometry' | 'material',
  preferredLayerId?: string,
): boolean {
  const ownerScene = getLayerOwnerSceneFor(appState, componentId);
  if (!ownerScene) return false;

  if (preferredLayerId && appState.layerLibrary[preferredLayerId]) {
    const layerNode = Object.values(ownerScene.nodes).find(node => node.kind === 'layer' && node.layerId === preferredLayerId);
    if (layerNode?.kind === 'layer') {
      return loadLayerSlotFor(appState, componentId, preferredLayerId, slot);
    }
  }

  const first = findFirstLayerNodeIds(ownerScene);
  return first ? loadLayerSlotFor(appState, componentId, first.layerId, slot) : false;
}

export interface LoadEditorGraphHooks {
  syncEditor?: () => boolean;
}

export function loadLibraryItemFor(
  appState: AppState,
  kind: 'geometry' | 'material',
  id: string,
  hooks: LoadEditorGraphHooks = {},
): boolean {
  const lib = lookupLibraryItemFor(appState, kind, id);

  if (!lib) {
    console.warn(`Library item not found: ${formatStableIdDebugLabel(id)}`);
    return false;
  }

  hooks.syncEditor?.();

  appState.editorContext = kind === 'geometry'
    ? { mode: 'geometry-library', libraryItemId: id }
    : { mode: 'material-library', libraryItemId: id };
  loadLibraryGraphIntoEditorFor(appState, kind, lib);

  return true;
}

function runtimeGraphFor(appState: AppState, kind: RuntimeGraphKind, graphId: string) {
  if (kind === 'data') return appState.dataGraphs[graphId];
  if (kind === 'event') return appState.eventGraphs[graphId];
  return appState.queryGraphs[graphId];
}

function deriveRuntimeNodeMetadata(appState: AppState, kind: RuntimeGraphKind, nodes: GraphNode[]): GraphNode[] {
  if (kind !== 'event') return nodes;
  return nodes.map(node => {
    if (node.type !== 'Set' && node.type !== 'State') return node;
    const stateName = String(node.data.state ?? '');
    const declaration = appState.runtimeState[stateName];
    if (!declaration) return node;
    return { ...node, data: { ...node.data, valueType: declaration.type } };
  });
}

export function loadRuntimeGraphFor(
  appState: AppState,
  kind: RuntimeGraphKind,
  graphId: string,
  hooks: LoadEditorGraphHooks = {},
): boolean {
  const graph = runtimeGraphFor(appState, kind, graphId);
  if (!graph) return false;
  hooks.syncEditor?.();
  appState.editorContext = { mode: `${kind}-library`, libraryItemId: graphId } as AppState['editorContext'];
  if (kind === 'data') appState.activeDataGraphId = graphId;
  else if (kind === 'event') appState.activeEventGraphId = graphId;
  else appState.activeQueryGraphId = graphId;
  appState.editorGraphKind = kind;
  appState.nodes = deriveRuntimeNodeMetadata(appState, kind, cloneNodes(graph.nodes));
  appState.edges = cloneEdges(graph.edges);
  if (shouldAutoPlaceNodes(appState.nodes)) {
    autoPlaceNodes(appState.nodes, appState.edges, nodeDefsForKind(kind));
  }
  appState.editorGraphDirty = false;
  appState.selectedNode = null;
  return true;
}

export function syncEditorFor(appState: AppState): boolean {
  const ctx = appState.editorContext;
  if (!appState.editorGraphDirty) return false;

  if (ctx.mode === 'data-library' || ctx.mode === 'event-library' || ctx.mode === 'query-library') {
    const kind = ctx.mode.slice(0, -'-library'.length) as RuntimeGraphKind;
    const graph = runtimeGraphFor(appState, kind, ctx.libraryItemId);
    if (!graph) return false;
    graph.nodes = cloneNodes(appState.nodes);
    graph.edges = cloneEdges(appState.edges);
    appState.editorGraphDirty = false;
    return true;
  }

  if (ctx.mode === 'geometry-library' || ctx.mode === 'material-library') {
    const kind: EditorGraphSlot = ctx.mode === 'geometry-library' ? 'geometry' : 'material';
    const lib = kind === 'geometry' ? appState.geometryLibrary.user : appState.materialLibrary.user;
    const item = lib[ctx.libraryItemId];
    if (!item || item.builtIn) return false;

    syncOutputNodeMetadata(appState.nodes, kind);
    item.nodes = cloneNodes(appState.nodes);
    item.edges = cloneEdges(appState.edges);
    appState.editorGraphDirty = false;
    appState.editorGraphKind = kind;
    return true;
  }

  if (ctx.mode === 'layer') {
    if (!ctx.layerId) return false;
    const layerDef = appState.layerLibrary[ctx.layerId];
    if (!layerDef) return false;

    const currentSlot = ctx.layerSlot === 'geometry' ? layerDef.geometry : layerDef.material;
    if (currentSlot.type === 'library') return false;

    if (ctx.layerSlot === 'geometry') {
      syncOutputNodeMetadata(appState.nodes, 'geometry');
      layerDef.geometry = {
        type: 'inline',
        nodes: cloneNodes(appState.nodes),
        edges: cloneEdges(appState.edges),
        sizeMode: 'sizeMode' in currentSlot ? (currentSlot as { sizeMode?: IntrinsicSizeMode }).sizeMode : undefined,
      };
    } else {
      syncOutputNodeMetadata(appState.nodes, 'material');
      layerDef.material = {
        type: 'inline',
        nodes: cloneNodes(appState.nodes),
        edges: cloneEdges(appState.edges),
      };
    }
    appState.editorGraphDirty = false;
    appState.editorGraphKind = ctx.layerSlot;
    return true;
  }

  return false;
}
